// assets/recommendations-quick-add.js
(() => {
  const SEL = {
    card: "[data-quick-add-card]",
    button: "[data-quick-add-button]",
    status: "[data-quick-add-status]",
    swatch: "[data-quick-add-swatch]",
    hiddenVariant: "[data-quick-add-variant]",
  };

  const CART_ADD_URL = "/cart/add.js";

  // UX timings
  const SUCCESS_TEXT = "Added to cart!";
  const SUCCESS_HOLD_MS = 2000;
  const FADE_DURATION_MS = 350;

  // ------------------------
  // helpers
  // ------------------------
  const setStatus = (card, msg = "") => {
    const el = card?.querySelector?.(SEL.status);
    if (el) el.textContent = msg;
  };

  const setButtonText = (btn, text) => {
    if (!btn) return;
    btn.textContent = text;
  };

  const rememberOriginalButtonText = (btn) => {
    if (!btn) return;
    if (!btn.dataset.quickAddOriginalText) {
      btn.dataset.quickAddOriginalText = (btn.textContent || "").trim() || "ADD";
    }
  };

  const restoreOriginalButtonText = (btn) => {
    if (!btn) return;
    const orig = btn.dataset.quickAddOriginalText;
    if (orig) btn.textContent = orig;
  };

  const getVariantId = (card) => {
    if (!card) return null;

    const swatch = card.querySelector(`${SEL.swatch}:checked`);
    const fromSwatch = swatch?.dataset?.variantId;
    if (fromSwatch) return fromSwatch;

    const hidden = card.querySelector(SEL.hiddenVariant);
    if (hidden?.value) return hidden.value;

    return null;
  };

  // Find the outer product "card" element that should fade
  const getProductCardRoot = (fromEl) => {
    if (!fromEl) return null;

    return (
      fromEl.closest(".grid-product") ||
      fromEl.closest(".grid__item") ||
      fromEl.closest(".product-recommendations__slide") ||
      null
    );
  };

  const fadeOutAndRemove = (el) => {
    if (!el) return;

    if (el.dataset.quickAddFading === "1") return;
    el.dataset.quickAddFading = "1";

    el.style.transition = `opacity ${FADE_DURATION_MS}ms ease, height ${FADE_DURATION_MS}ms ease, margin ${FADE_DURATION_MS}ms ease, padding ${FADE_DURATION_MS}ms ease`;
    el.style.opacity = "1";

    const startHeight = el.getBoundingClientRect().height;
    el.style.height = `${startHeight}px`;
    el.style.overflow = "hidden";

    requestAnimationFrame(() => {
      el.style.opacity = "0";
      el.style.height = "0px";
      el.style.marginTop = "0px";
      el.style.marginBottom = "0px";
      el.style.paddingTop = "0px";
      el.style.paddingBottom = "0px";
    });

    window.setTimeout(() => {
      try {
        el.remove();
      } catch (e) {
        // no-op
      }
    }, FADE_DURATION_MS + 50);
  };

  // Shopify sometimes returns JSON with content-type "text/javascript"
  const isJsonish = (res) => {
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    return ct.includes("application/json") || ct.includes("text/javascript");
  };

  const readJsonOrThrowHelpful = async (res) => {
    if (isJsonish(res)) return res.json();

    const text = await res.text().catch(() => "");
    const snippet = text.slice(0, 220).replace(/\s+/g, " ");
    throw new Error(
      `Expected JSON but got "${res.headers.get("content-type") || "unknown"}" (HTTP ${res.status}). Snippet: ${snippet}`
    );
  };

  const addToCart = async (variantId, quantity = 1) => {
    const id = Number(variantId);
    if (!id || Number.isNaN(id)) {
      throw new Error("Invalid variant. Please try again.");
    }

    const res = await fetch(CART_ADD_URL, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, text/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({
        items: [{ id, quantity }],
      }),
    });

    const data = await readJsonOrThrowHelpful(res);

    if (!res.ok) {
      throw new Error(
        data?.description ||
          data?.message ||
          `Could not add item to cart (HTTP ${res.status}).`
      );
    }

    return data;
  };

  /**
   * THEME-NATIVE CART REFRESH
   */
  const refreshThemeCart = () => {
    try {
      document.dispatchEvent(new CustomEvent("cart:build"));
    } catch (e) {
      // no-op
    }

    try {
      document.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (e) {
      // no-op
    }
  };

  const fallbackUpdateBubbleCount = async () => {
    const getCart = window.theme?.cart?.getCart;
    if (!getCart) return;

    const cart = await getCart().catch(() => null);
    const count = cart?.item_count;
    if (typeof count !== "number") return;

    document.querySelectorAll(".cart-link__bubble-num").forEach((el) => {
      el.innerText = String(count);
    });

    document.querySelectorAll(".cart-link__bubble").forEach((b) => {
      if (count > 0) b.classList.add("cart-link__bubble--visible");
      else b.classList.remove("cart-link__bubble--visible");
    });
  };

  // ------------------------
  // UX: clear status + restore button label on size change
  // ------------------------
  document.addEventListener("change", (e) => {
    const swatch = e.target?.closest?.(SEL.swatch);
    if (!swatch) return;

    const card = swatch.closest(SEL.card);
    if (!card) return;

    setStatus(card, "");

    const btn = card.querySelector(SEL.button);
    restoreOriginalButtonText(btn);
  });

  // ------------------------
  // click handler (delegated, capture)
  // ------------------------
  document.addEventListener(
    "click",
    async (e) => {
      const btn = e.target?.closest?.(SEL.button);
      if (!btn) return;

      const card = btn.closest(SEL.card);
      if (!card) return;

      e.preventDefault();

      const variantId = getVariantId(card);
      if (!variantId) {
        setStatus(card, "Please select a size.");
        return;
      }

      if (btn.dataset.quickAddBusy === "1") return;
      btn.dataset.quickAddBusy = "1";

      rememberOriginalButtonText(btn);

      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");

      setStatus(card, "");
      setButtonText(btn, "Adding…");

      try {
        await addToCart(variantId, 1);

        refreshThemeCart();
        await fallbackUpdateBubbleCount();

        // ✅ Success UX: button text + fade card after 2s
        setButtonText(btn, SUCCESS_TEXT);

        const root = getProductCardRoot(card);
        window.setTimeout(() => {
          // fadeOutAndRemove(root);
        }, SUCCESS_HOLD_MS);
      } catch (err) {
        console.error("[recommendations quick add] failed:", err);

        restoreOriginalButtonText(btn);
        setStatus(card, err?.message || "Error adding to cart.");
      } finally {
        // Only re-enable if we're NOT in the success flow
        if (btn.textContent !== SUCCESS_TEXT) {
          btn.disabled = false;
          btn.removeAttribute("aria-busy");
          btn.dataset.quickAddBusy = "0";
        } else {
          btn.removeAttribute("aria-busy");
          // keep busy=1 until card fades out
        }
      }
    },
    true
  );
})();
